import workspaceCatalog from '../../../config/insightWorkspaceCatalog.json'

function formatScopeLabel(scopeLabel = '') {
  return String(scopeLabel || '').trim() || 'Manager + Team'
}

function formatClientLabel(selectedClientName = '') {
  return String(selectedClientName || '').trim() || 'All clients'
}

function formatTeamMemberLabel(selectedTeamMemberName = '') {
  return String(selectedTeamMemberName || '').trim() || 'All team members'
}

function formatDateRangeLabel(dateRangeLabel = '') {
  return String(dateRangeLabel || '').trim() || 'Current date range'
}

function formatSourceLabel(sourceDataLabel = '') {
  return String(sourceDataLabel || '').trim() || 'ConnectWise PSA data'
}

function buildContextInstruction({
  scopeLabel = '',
  selectedClientName = '',
  selectedTeamMemberName = '',
  dateRangeLabel = '',
  sourceDataLabel = '',
} = {}) {
  const clientLabel = formatClientLabel(selectedClientName)
  const teamMemberLabel = formatTeamMemberLabel(selectedTeamMemberName)

  return [
    '',
    'Use this active workspace context for the answer:',
    `- Scope: ${formatScopeLabel(scopeLabel)}`,
    `- Client: ${clientLabel}`,
    `- Team member: ${teamMemberLabel}`,
    `- Date range: ${formatDateRangeLabel(dateRangeLabel)}`,
    `- Source data: ${formatSourceLabel(sourceDataLabel)}`,
    selectedClientName && selectedTeamMemberName
      ? '- If the client and team member intersection is too narrow to support the request, say so explicitly.'
      : '- If any required context is missing from the data slice, say exactly what is missing.',
  ].join('\n')
}

function withContext(prompt, context = {}) {
  return `${String(prompt || '').trim()}${buildContextInstruction(context)}`.trim()
}

function appendContextHelper(baseHelper = '', context = {}) {
  const parts = [String(baseHelper || '').trim()].filter(Boolean)

  if (context.selectedClientName) {
    parts.push(`For ${context.selectedClientName}.`)
  } else if (context.selectedTeamMemberName) {
    parts.push(`For ${context.selectedTeamMemberName}.`)
  }

  return parts.join(' ')
}

function buildWorkflowPrompt(action) {
  const evidencePriorities = Array.isArray(action?.evidencePriorities)
    ? action.evidencePriorities.filter(Boolean)
    : []
  const avoidItems = Array.isArray(action?.avoid)
    ? action.avoid.filter(Boolean)
    : []
  const instruction = String(action?.instruction || '').trim()
  const outputEmphasis = String(action?.outputEmphasis || '').trim()
  const userGoal = String(action?.userGoal || '').trim()
  const focusArea = String(action?.focusAreaLabel || '').trim()
  const workflowName = String(action?.label || '').trim()

  return [
    `Generate an Insight Workspace report for the workflow "${workflowName}".`,
    focusArea ? `Focus area: ${focusArea}.` : '',
    userGoal ? `User goal: ${userGoal}` : '',
    evidencePriorities.length
      ? `Prioritize evidence from: ${evidencePriorities.join(', ')}.`
      : '',
    outputEmphasis ? `Output emphasis: ${outputEmphasis}` : '',
    avoidItems.length ? `Avoid: ${avoidItems.join('; ')}.` : '',
    instruction,
  ]
    .filter(Boolean)
    .join(' ')
}

function buildDisplayQuestion(action, context = {}) {
  if (!action) return ''

  if (action.id === 'prepare-qbr') {
    return context.selectedClientName
      ? `Internal QBR Prep — ${context.selectedClientName}`
      : 'Internal QBR Prep'
  }

  if (action.id === 'prepare-one-on-one') {
    return context.selectedTeamMemberName
      ? `Prepare for a 1:1 for ${context.selectedTeamMemberName}.`
      : 'Prepare for a 1:1.'
  }

  return String(action.shortQuestion || action.label || '').trim()
}

// quickStartActionIds decides which quick-start buttons show, so an action can be
// hidden without deleting it.
function resolveQuickStartActions() {
  const actions = Array.isArray(workspaceCatalog.quickStartActions) ? workspaceCatalog.quickStartActions : []
  const visibleIds = Array.isArray(workspaceCatalog.quickStartActionIds) ? workspaceCatalog.quickStartActionIds : null
  return visibleIds ? actions.filter((action) => visibleIds.includes(action.id)) : actions
}

export function getWorkspaceConfig() {
  return {
    quickStartActions: resolveQuickStartActions(),
    workspaceAreas: Array.isArray(workspaceCatalog.focusAreas)
      ? workspaceCatalog.focusAreas.map((area) => ({
          ...area,
          actions: Array.isArray(workspaceCatalog.workflowActions)
            ? workspaceCatalog.workflowActions.filter((action) => action.focusAreaId === area.id)
            : [],
        }))
      : [],
    workflowActions: [],
  }
}

export function resolveWorkspaceActionQuestion(scopeType, action, context = {}) {
  if (!action) return ''

  const prompt = String(action.prompt || buildWorkflowPrompt(action, context)).trim()
  return withContext(prompt, context)
}

export function resolveWorkspaceActionDisplayQuestion(scopeType, action, context = {}) {
  return buildDisplayQuestion(action, context)
}

export function resolveWorkspaceActionHelperText(scopeType, action, context = {}) {
  if (!action) return ''
  return appendContextHelper(action.helper, context)
}
